import React, { useCallback, useEffect, useRef, useState } from "react";

import {
  api,
  type KnowledgeItemData,
  type KnowledgeItemStatus,
} from "../lib/api";
import { logError, toUserMessage } from "../lib/errors";
import {
  getKnowledgeFailureDisplayMessage,
  getKnowledgeItemDisplayName,
  getKnowledgeLibraryStatusLabel,
  getKnowledgeStatusLabel,
  getKnowledgeSummaryStatusLabel,
  getKnowledgeTypeLabel,
  getKnowledgeUnavailableReason,
  isKnowledgeItemCompanyResearchUsable,
  isKnowledgeItemGroupTalkUsable,
  isKnowledgeItemPollingStatus,
  isKnowledgeItemUsable,
  shouldShowKnowledgeSummaryStatus,
  validateKnowledgePdfFile,
} from "../lib/knowledge";

export type KnowledgeAttachmentPickerMode = "default" | "groupTalk" | "companyResearch";

type KnowledgeAttachmentPickerProps = {
  value: KnowledgeItemData | null;
  onChange: (item: KnowledgeItemData | null) => void;
  mode?: KnowledgeAttachmentPickerMode;
  allowedTypes?: readonly KnowledgeItemData["type"][];
  showUpload?: boolean;
  allowDelete?: boolean;
  compact?: boolean;
  heading?: string;
  description?: string;
  usageNote?: React.ReactNode;
  emptyMessage?: string;
  selectedLabel?: string;
  detachLabel?: string;
  pollingMessage?: string;
  unavailableFallbackMessage?: string;
};

const POLLING_INTERVAL_MS = 2000;
const MAX_POLLING_ATTEMPTS = 30;
const DEFAULT_PICKER_KNOWLEDGE_TYPES = ["pdf", "executive_memo"] as const;
const GROUP_TALK_PICKER_KNOWLEDGE_TYPES = [...DEFAULT_PICKER_KNOWLEDGE_TYPES, "url"] as const;

function pickerAllowedTypes(mode: KnowledgeAttachmentPickerMode): readonly string[] {
  return mode === "groupTalk" ? GROUP_TALK_PICKER_KNOWLEDGE_TYPES : DEFAULT_PICKER_KNOWLEDGE_TYPES;
}

function isPickerItemUsable(item: KnowledgeItemData, mode: KnowledgeAttachmentPickerMode): boolean {
  if (mode === "groupTalk") return isKnowledgeItemGroupTalkUsable(item);
  if (mode === "companyResearch") return isKnowledgeItemCompanyResearchUsable(item);
  return isKnowledgeItemUsable(item);
}

function getPickerKnowledgeStatusLabel(
  item: KnowledgeItemData,
  mode: KnowledgeAttachmentPickerMode
): string {
  return (mode === "groupTalk" || mode === "companyResearch") && item.type === "url"
    ? getKnowledgeLibraryStatusLabel(item)
    : getKnowledgeStatusLabel(item.status);
}

function buildLocalKnowledgeItem(params: {
  id: string;
  filename: string;
  status: KnowledgeItemStatus;
  sizeBytes?: number;
}): KnowledgeItemData {
  const title = params.filename.replace(/\.pdf$/i, "") || params.filename || "PDF資料";
  return {
    id: params.id,
    type: "pdf",
    title,
    status: params.status,
    visibility: "private",
    original_filename: params.filename,
    mime_type: "application/pdf",
    size_bytes: params.sizeBytes ?? null,
    page_count: null,
    text_extractable: null,
    ai_processing_allowed: true,
    failure_reason: null,
    failure_code: null,
    failure_stage: null,
    failure_message: null,
    can_retry: false,
    summary: null,
    has_summary: false,
    summarized_at: null,
    created_at: null,
    updated_at: null,
  };
}

function formatDateTime(value: string | null | undefined): string {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return new Intl.DateTimeFormat("ja-JP", {
    month: "numeric",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  }).format(date);
}

export function KnowledgeAttachmentPicker({
  value,
  onChange,
  mode = "default",
  allowedTypes: explicitAllowedTypes,
  showUpload = true,
  allowDelete = true,
  compact = false,
  heading = "参考資料",
  description,
  usageNote,
  emptyMessage = "参考資料は未選択です。ナレッジなしでも壁打ちは始められます。",
  selectedLabel = "添付中のナレッジ",
  detachLabel = "この相談から外す",
  pollingMessage = "ナレッジを読み取り中です。完了すると、このナレッジを前提に壁打ちできます。",
  unavailableFallbackMessage = "このナレッジはまだ利用できません。利用可能になってから開始できます。",
}: KnowledgeAttachmentPickerProps) {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const pollingTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pollingAbortRef = useRef<AbortController | null>(null);
  const mountedRef = useRef(true);
  const onChangeRef = useRef(onChange);

  const [uploading, setUploading] = useState(false);
  const [message, setMessage] = useState("");
  const [libraryOpen, setLibraryOpen] = useState(false);
  const [libraryLoading, setLibraryLoading] = useState(false);
  const [libraryItems, setLibraryItems] = useState<KnowledgeItemData[]>([]);
  const allowedTypes = explicitAllowedTypes ?? pickerAllowedTypes(mode);
  const allowedTypeSet = new Set<string>(allowedTypes);
  const allowsUrl = allowedTypeSet.has("url");
  const renderedDescription = description ?? (
    allowsUrl
      ? "PDF、Webページ、または社長メモを1件添付できます。選び直すと差し替わります。"
      : "PDFまたは社長メモを1件添付できます。選び直すと差し替わります。"
  );
  const pollingItemId = value && isKnowledgeItemPollingStatus(value.status) ? value.id : "";

  useEffect(() => {
    onChangeRef.current = onChange;
  }, [onChange]);

  const stopPolling = useCallback(() => {
    if (pollingTimeoutRef.current !== null) {
      clearTimeout(pollingTimeoutRef.current);
      pollingTimeoutRef.current = null;
    }
    if (pollingAbortRef.current !== null) {
      pollingAbortRef.current.abort();
      pollingAbortRef.current = null;
    }
  }, []);

  const pollKnowledgeItem = useCallback((itemId: string, attempt = 1) => {
    stopPolling();
    const controller = typeof AbortController !== "undefined" ? new AbortController() : null;
    pollingAbortRef.current = controller;

    api.getKnowledgeItem(itemId, controller?.signal)
      .then((item) => {
        if (!mountedRef.current) return;
        onChangeRef.current(item);
        if (item.status === "ready" || item.status === "failed" || item.status === "deleted") {
          stopPolling();
          return;
        }
        if (attempt >= MAX_POLLING_ATTEMPTS) {
          setMessage("読み取り完了を確認できませんでした。少し時間を置いてナレッジライブラリから確認してください。");
          stopPolling();
          return;
        }
        pollingTimeoutRef.current = setTimeout(() => pollKnowledgeItem(itemId, attempt + 1), POLLING_INTERVAL_MS);
      })
      .catch((error) => {
        if (!mountedRef.current || controller?.signal.aborted) return;
        logError(error);
        setMessage(`${toUserMessage(error)} 読み取り状況を確認できませんでした。`);
        stopPolling();
      });
  }, [stopPolling]);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      stopPolling();
    };
  }, [stopPolling]);

  useEffect(() => {
    if (pollingItemId) {
      pollKnowledgeItem(pollingItemId);
      return;
    }
    stopPolling();
  }, [pollKnowledgeItem, pollingItemId, stopPolling]);

  const handleUploadClick = () => {
    inputRef.current?.click();
  };

  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.currentTarget.files?.[0] ?? null;
    event.currentTarget.value = "";
    if (!file || uploading) return;

    const validationError = validateKnowledgePdfFile(file);
    if (validationError) {
      setMessage(validationError);
      return;
    }

    setUploading(true);
    setMessage("");
    stopPolling();

    let knowledgeItemId = "";
    try {
      const intent = await api.createKnowledgePdfUploadIntent({
        filename: file.name,
        mimeType: file.type || "application/pdf",
        sizeBytes: file.size,
      });
      knowledgeItemId = intent.knowledgeItemId;
      onChange(buildLocalKnowledgeItem({
        id: knowledgeItemId,
        filename: file.name,
        status: "uploading",
        sizeBytes: file.size,
      }));
      const safeUploadHeaders = Object.fromEntries(
        Object.entries(intent.uploadHeaders ?? {}).filter(
          ([key]) => key.toLowerCase() !== "authorization"
        )
      );

      const putResponse = await fetch(intent.uploadUrl, {
        method: "PUT",
        headers: {
          ...safeUploadHeaders,
          "Content-Type": "application/pdf",
        },
        body: file,
      });
      if (!putResponse.ok) {
        throw new Error("PDFのアップロードに失敗しました。通信状況を確認してもう一度お試しください。");
      }

      const completed = await api.completeKnowledgePdfUpload(knowledgeItemId);
      onChange(buildLocalKnowledgeItem({
        id: knowledgeItemId,
        filename: file.name,
        status: completed.status,
        sizeBytes: file.size,
      }));
    } catch (error) {
      logError(error);
      if (knowledgeItemId) {
        onChange(null);
      }
      setMessage(`${toUserMessage(error)} もう一度アップロードできます。`);
    } finally {
      if (mountedRef.current) {
        setUploading(false);
      }
    }
  };

  const loadLibrary = async () => {
    setLibraryOpen(true);
    setLibraryLoading(true);
    setMessage("");
    try {
      const response = await api.listKnowledgeItems({ type: allowedTypes.join(","), limit: 20 });
      setLibraryItems(response.items.filter(
        (item) => allowedTypeSet.has(item.type) && item.status !== "deleted"
      ));
    } catch (error) {
      logError(error);
      setMessage(`${toUserMessage(error)} ナレッジライブラリを読み込めませんでした。`);
    } finally {
      setLibraryLoading(false);
    }
  };

  const selectExistingItem = (item: KnowledgeItemData) => {
    if (!allowedTypeSet.has(item.type) || !isPickerItemUsable(item, mode)) return;
    setMessage("");
    onChange(item);
    setLibraryOpen(false);
  };

  const detachCurrentItem = () => {
    stopPolling();
    setMessage("");
    onChange(null);
  };

  const deleteItem = async (item: KnowledgeItemData) => {
    const itemName = getKnowledgeItemDisplayName(item);
    const confirmed = window.confirm(
      `「${itemName}」をナレッジライブラリから削除します。元の社長メモやPDFの元データは削除されません。`
    );
    if (!confirmed) return;

    try {
      await api.deleteKnowledgeItem(item.id);
      setLibraryItems((current) => current.filter((entry) => entry.id !== item.id));
      if (value?.id === item.id) {
        detachCurrentItem();
      }
    } catch (error) {
      logError(error);
      setMessage(`${toUserMessage(error)} 削除できませんでした。`);
    }
  };

  const currentName = value ? getKnowledgeItemDisplayName(value) : "";
  const currentTypeLabel = value ? getKnowledgeTypeLabel(value.type) : "";
  const currentFailure = value?.status === "failed"
    ? getKnowledgeFailureDisplayMessage(value)
    : "";
  const currentUnavailableReason = value && !(mode === "groupTalk" && value.type === "url")
    ? getKnowledgeUnavailableReason(value)
    : "";
  const cannotUseCurrent = value !== null && (
    !allowedTypeSet.has(value.type) || !isPickerItemUsable(value, mode)
  );
  const defaultUsageNote = (
    <>
      <p>
        {allowsUrl
          ? "PDFやWebページ、社長メモは、あなたの相談や分析に使用されます。"
          : "アップロードしたPDFや社長メモは、あなたの相談に回答するために使用されます。"}
      </p>
      <p>他のユーザーに共有されることはありません。ナレッジはいつでも削除できます。</p>
      <p className="mt-1">
        ナレッジを読み取って要約する際には、その内容をAI処理に使用します。
        壁打ちの会話中は、相談に必要な要約や関連箇所を参照します。
      </p>
    </>
  );
  const renderedUsageNote = usageNote === undefined ? defaultUsageNote : usageNote;

  return (
    <section className={compact
      ? "mt-4 rounded-2xl border border-[#EADBC8] bg-[#FFFDFB] px-3 py-3"
      : "mt-5 rounded-[1.3rem] border border-[#EADBC8] bg-[#FFFDFB] px-4 py-4"
    }>
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <h3 className="text-base font-black text-[#5D4D46]">{heading}</h3>
          <p className="mt-1 text-sm leading-6 text-[#6B5851]">
            {renderedDescription}
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          {showUpload ? (
            <>
              <input
                ref={inputRef}
                aria-label="PDFをアップロード"
                type="file"
                accept="application/pdf,.pdf"
                onChange={(event) => void handleFileChange(event)}
                className="sr-only"
              />
              <button
                type="button"
                onClick={handleUploadClick}
                disabled={uploading}
                className="min-h-[40px] rounded-full bg-[#5D4D46] px-4 py-2 text-sm font-black text-white transition hover:bg-[#4A3E39] disabled:cursor-not-allowed disabled:opacity-60"
              >
                {uploading ? "アップロード中..." : "PDFをアップロード"}
              </button>
            </>
          ) : null}
          <button
            type="button"
            onClick={() => void loadLibrary()}
            className="min-h-[40px] rounded-full border border-[#D9C5B9] bg-white px-4 py-2 text-sm font-black text-[#6B5851] transition hover:bg-[#FFF7F1]"
          >
            ナレッジライブラリから選ぶ
          </button>
        </div>
      </div>

      {renderedUsageNote ? (
        <div className="mt-3 rounded-[1rem] bg-white px-3 py-3 text-xs leading-5 text-[#7B6861]">
          {renderedUsageNote}
        </div>
      ) : null}

      {value ? (
        <div className="mt-4 rounded-[1.1rem] border border-[#EADBC8] bg-white p-4">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
            <div className="min-w-0">
              <p className="text-xs font-black text-[#A67C52]">{selectedLabel}</p>
              <p className="mt-1 break-words text-sm font-black text-[#5D4D46]">{currentName}</p>
              <p className="mt-1 text-sm font-bold text-[#6B5851]">
                種別: {currentTypeLabel}
              </p>
              {value.type === "url" && value.domain ? (
                <p className="mt-1 break-words text-xs font-bold text-[#8F7468] [overflow-wrap:anywhere]">
                  {value.domain}
                </p>
              ) : null}
              <p className="mt-1 text-sm font-bold text-[#6B5851]">
                状態: {getPickerKnowledgeStatusLabel(value, mode)}
              </p>
              {value.page_count ? (
                <p className="mt-1 text-xs font-bold text-[#8F7468]">{value.page_count}ページ</p>
              ) : null}
              {shouldShowKnowledgeSummaryStatus(value) ? (
                <p className="mt-1 text-xs font-bold text-[#8F7468]">{getKnowledgeSummaryStatusLabel(value)}</p>
              ) : null}
            </div>
            <div className="flex shrink-0 flex-wrap gap-2">
              <button
                type="button"
                onClick={detachCurrentItem}
                className="min-h-[36px] rounded-full border border-[#EADBC8] bg-white px-3 py-1.5 text-xs font-black text-[#8F7468] transition hover:bg-[#FFF7F1]"
              >
                {detachLabel}
              </button>
              {allowDelete ? (
                <button
                  type="button"
                  aria-label={`削除 ${currentName}`}
                  onClick={() => void deleteItem(value)}
                  className="min-h-[36px] rounded-full border border-[#EADBC8] bg-white px-3 py-1.5 text-xs font-black text-[#8F7468] transition hover:bg-[#FFF7F1]"
                >
                  削除
                </button>
              ) : null}
            </div>
          </div>
          {isKnowledgeItemPollingStatus(value.status) ? (
            <p className="mt-3 rounded-[0.9rem] bg-[#FFF7F1] px-3 py-2 text-sm font-bold text-[#8A6558]">
              {pollingMessage}
            </p>
          ) : null}
          {cannotUseCurrent ? (
            <p className="mt-3 text-sm font-bold text-[#C06D5A]">
              {currentFailure || currentUnavailableReason || unavailableFallbackMessage}
            </p>
          ) : null}
        </div>
      ) : (
        <p className="mt-4 rounded-[1rem] border border-dashed border-[#D9C5B9] bg-white px-3 py-3 text-sm font-bold text-[#8F7468]">
          {emptyMessage}
        </p>
      )}

      {message ? (
        <p role="alert" className="mt-3 rounded-[1rem] border border-[#F0C8BA] bg-[#FFF7F1] px-3 py-2 text-sm font-bold text-[#C06D5A]">
          {message}
        </p>
      ) : null}

      {libraryOpen ? (
        <div className="mt-4 rounded-[1.1rem] border border-[#EADBC8] bg-white p-4">
          <div className="flex items-center justify-between gap-3">
            <h4 className="text-sm font-black text-[#5D4D46]">ナレッジライブラリ</h4>
            <button
              type="button"
              onClick={() => setLibraryOpen(false)}
              className="rounded-full border border-[#EADBC8] px-3 py-1 text-xs font-black text-[#8F7468]"
            >
              閉じる
            </button>
          </div>
          {libraryLoading ? (
            <p className="mt-3 text-sm font-bold text-[#8F7468]">読み込み中...</p>
          ) : libraryItems.length === 0 ? (
            <p className="mt-3 text-sm font-bold text-[#8F7468]">ナレッジライブラリに表示できる項目はまだありません。</p>
          ) : (
            <div className="mt-3 space-y-3">
              {libraryItems.map((item) => {
                const usable = isPickerItemUsable(item, mode);
                const name = getKnowledgeItemDisplayName(item);
                const typeLabel = getKnowledgeTypeLabel(item.type);
                return (
                  <div key={item.id} className="rounded-[1rem] border border-[#EADBC8] bg-[#FFFDFB] p-3">
                    <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                      <div className="min-w-0">
                        <p className="text-xs font-black text-[#A67C52]">{typeLabel}</p>
                        <p className="break-words text-sm font-black text-[#5D4D46]">{name}</p>
                        {item.type === "url" && item.domain ? (
                          <p className="mt-1 break-words text-xs font-bold text-[#8F7468] [overflow-wrap:anywhere]">
                            {item.domain}
                          </p>
                        ) : null}
                        <p className="mt-1 text-xs font-bold text-[#8F7468]">
                          状態: {getPickerKnowledgeStatusLabel(item, mode)}
                          {item.page_count ? ` / ${item.page_count}ページ` : ""}
                          {shouldShowKnowledgeSummaryStatus(item)
                            ? ` / ${getKnowledgeSummaryStatusLabel(item)}`
                            : ""}
                        </p>
                        {item.summarized_at ? (
                          <p className="mt-1 text-xs text-[#8F7468]">要約: {formatDateTime(item.summarized_at)}</p>
                        ) : item.created_at ? (
                          <p className="mt-1 text-xs text-[#8F7468]">作成: {formatDateTime(item.created_at)}</p>
                        ) : null}
                        {item.status === "failed" ? (
                          <p className="mt-2 break-words text-xs font-bold text-[#C06D5A] [overflow-wrap:anywhere]">
                            {getKnowledgeFailureDisplayMessage(item)}
                          </p>
                        ) : null}
                      </div>
                      <div className="flex shrink-0 flex-wrap gap-2">
                        <button
                          type="button"
                          aria-label={`選ぶ ${name}`}
                          onClick={() => selectExistingItem(item)}
                          disabled={!usable}
                          className="min-h-[34px] rounded-full bg-[#5D4D46] px-3 py-1.5 text-xs font-black text-white transition hover:bg-[#4A3E39] disabled:cursor-not-allowed disabled:opacity-50"
                        >
                          選ぶ
                        </button>
                        {allowDelete ? (
                          <button
                            type="button"
                            aria-label={`削除 ${name}`}
                            onClick={() => void deleteItem(item)}
                            className="min-h-[34px] rounded-full border border-[#EADBC8] bg-white px-3 py-1.5 text-xs font-black text-[#8F7468] transition hover:bg-[#FFF7F1]"
                          >
                            削除
                          </button>
                        ) : null}
                      </div>
                    </div>
                    {!usable ? (
                      <p className="mt-2 text-xs font-bold text-[#8F7468]">
                        {getKnowledgeUnavailableReason(item) || "利用可能になったナレッジだけ選べます。"}
                      </p>
                    ) : null}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      ) : null}
    </section>
  );
}
